import { Button } from "@/components/ui/Button";
import { Card } from "@/components/ui/Card";
import { PageHeader } from "@/components/ui/PageHeader";
import { Select } from "@/components/ui/Select";
import { useCharacters } from "@/features/characters/hooks/useCharacters";
import { usePreferences } from "@/features/settings/storage/usePreferences";
import { downloadTextFile } from "@/lib/format";
import type { StorageMode } from "@/features/settings/storage/preferences";

const storageOptions = [
  { label: "Automático (IndexedDB com fallback)", value: "auto" },
  { label: "Forçar IndexedDB", value: "indexeddb" },
  { label: "Forçar localStorage", value: "localstorage" },
];

export function SettingsPage() {
  const { items, engine, refresh, wipeCharacters } = useCharacters();
  const { storageMode, updateStorageMode } = usePreferences();

  function handleExport() {
    downloadTextFile("personagens-rpg.json", JSON.stringify(items, null, 2));
  }

  async function handleClear() {
    const confirmed = window.confirm(
      "Deseja apagar todas as fichas salvas localmente? Esta ação remove a coleção inteira.",
    );

    if (!confirmed) {
      return;
    }

    await wipeCharacters();
  }

  return (
    <div className="grid gap-8">
      <PageHeader
        eyebrow="Configurações"
        title="Preferências locais e governança da coleção"
        description="Ajuste a estratégia de armazenamento, exporte as fichas em JSON e mantenha a base preparada para uso offline completo no futuro."
      />

      <div className="grid gap-6 xl:grid-cols-[0.95fr_1.05fr]">
        <Card
          title="Persistência"
          subtitle="Defina como a aplicação prioriza o armazenamento no navegador."
        >
          <div className="grid gap-5">
            <Select
              label="Modo de armazenamento"
              options={storageOptions}
              value={storageMode}
              onChange={async (event) => {
                updateStorageMode(event.target.value as StorageMode);
                await refresh();
              }}
              helperText="O modo automático tenta IndexedDB primeiro e usa localStorage como fallback."
            />
            <div className="rounded-3xl border border-gold/15 bg-black/15 p-5">
              <p className="text-xs uppercase tracking-[0.24em] text-mist">Engine ativa</p>
              <p className="mt-3 text-2xl text-parchment">
                {engine === "indexeddb" ? "IndexedDB" : "localStorage"}
              </p>
              <p className="mt-2 text-sm leading-7 text-mist">
                Esta informação reflete o mecanismo realmente usado na sessão atual.
              </p>
            </div>
          </div>
        </Card>

        <Card
          title="Operações locais"
          subtitle="Ferramentas administrativas sem qualquer backend."
        >
          <div className="grid gap-5">
            <div className="rounded-3xl border border-gold/15 bg-black/15 p-5">
              <p className="text-xs uppercase tracking-[0.24em] text-mist">Exportação</p>
              <p className="mt-3 text-sm leading-7 text-mist">
                Exporte toda a coleção atual em JSON para backup manual, migração futura
                ou inspeção do estado local.
              </p>
              <Button className="mt-4" variant="secondary" onClick={handleExport}>
                Exportar JSON
              </Button>
            </div>

            <div className="rounded-3xl border border-red-400/20 bg-red-950/15 p-5">
              <p className="text-xs uppercase tracking-[0.24em] text-red-100">
                Limpeza total
              </p>
              <p className="mt-3 text-sm leading-7 text-red-100/80">
                Remove todas as fichas gravadas no mecanismo ativo. Use apenas quando
                desejar reiniciar a coleção local.
              </p>
              <Button className="mt-4" variant="danger" onClick={handleClear}>
                Apagar todas as fichas
              </Button>
            </div>

            <div className="rounded-3xl border border-gold/15 bg-black/15 p-5">
              <p className="text-xs uppercase tracking-[0.24em] text-mist">Resumo</p>
              <p className="mt-3 text-sm leading-7 text-mist">
                {items.length} personagem(ns) disponíveis localmente. O app já está
                estruturado para receber service worker e cache de assets sem mudar o
                modelo de dados.
              </p>
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
}
